import { Component } from '@theme/component';
import { debounce, enableScrollLock, disableScrollLock } from '@theme/utilities';
import { CartNotification } from './cart-notification.js';

// ========================================
// Constants
// ========================================

const DESKTOP_BREAKPOINT = 1024;
const MEGAMENU_VISIBILITY_DELAY = 250;
const MEGAMENU_SWITCH_DURATION = 600;
const SAFE_TRIANGLE_PADDING = 10;

/**
 * APC Track & Field Header Component
 * Manages megamenu interactions with hover behavior and mobile menu
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} menuitem - Menu items with submenus
 * @property {HTMLElement[]} megamenu - Megamenu dropdowns
 * @property {HTMLElement[]} menuToggle - Mobile menu toggle button and backdrop
 * @property {HTMLElement} container - Header container element
 * @property {HTMLElement} panelsContainer - Mobile panels container (fixed clipping boundary)
 * @property {HTMLElement} panelsWrapper - Mobile panels wrapper
 * @property {SVGElement} safeTriangle - Safe triangle SVG for megamenu hover
 * @property {SVGPathElement} safeTrianglePath - Safe triangle path element
 *
 * @extends {Component<Refs>}
 */
class APCTFHeader extends Component {
  // ========================================
  // Private Fields
  // ========================================

  /** @type {HTMLElement | null} */
  #activeMenuItem = null;

  /** @type {ReturnType<typeof debounce> | null} */
  #debouncedCloseMegamenu = null;

  /** @type {string | null} */
  #currentPanelId = 'mobile-panel-main';

  /** @type {number} */
  #prevScrollPos = 0;

  /** @type {number} - 0 = none, 1 = up, 2 = down */
  #scrollDirection = 0;

  /** @type {boolean} */
  #headerIsSticky = false;

  /** @type {boolean} */
  #megamenuOpen = false;

  /** @type {{ x: number, y: number }} */
  #mousePosition = { x: 0, y: 0 };

  /** @type {((e: MouseEvent) => void) | null} */
  #boundMouseMoveHandler = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {((e: KeyboardEvent) => void) | null} */
  #focusTrapHandler = null;

  /** @type {number} */
  #announcementBarHeight = 0;

  /** @type {number} */
  #hidingPoint = 0;

  /** @type {number} */
  #headerPaddingPx = 0;

  // ========================================
  // Lifecycle
  // ========================================

  connectedCallback() {
    super.connectedCallback();

    if (this.#activeMenuItem) {
      this.#closeMegamenu(this.#activeMenuItem);
      this.#activeMenuItem = null;
    }

    this.#debouncedCloseMegamenu = debounce(() => {
      if (this.#activeMenuItem) {
        this.#closeMegamenu(this.#activeMenuItem);
        this.#activeMenuItem = null;
      }
    }, MEGAMENU_VISIBILITY_DELAY);

    this.#setupMobileMenu();
    this.#setupDesktopKeyboardNavigation();
    this.#setupGlassMode();
    this.#setupStickyHeader();
    this.#setupResizeObserver();
    this.#calculateMegamenuHeights();
  }

  // ========================================
  // Layout & CSS Variables
  // ========================================

  /**
   * Set header container height as CSS variable on :root
   */
  #setHeaderContainerHeight() {
    const { container } = this.refs;
    if (!container) return;
    document.documentElement.style.setProperty('--header-container-height', `${container.offsetHeight}px`);
  }

  /**
   * Set announcement bar total height as CSS variable on :root
   */
  #setAnnouncementBarHeight() {
    const announcementBars = document.querySelectorAll('.announcement-bar');
    const totalHeight = Array.from(announcementBars).reduce((total, bar) => {
      return total + (bar instanceof HTMLElement ? bar.offsetHeight : 0);
    }, 0);
    document.documentElement.style.setProperty('--announcement-bar-total-height', `${totalHeight}px`);
    this.#announcementBarHeight = totalHeight;

    // Set initial announcement offset for sticky product card (accounts for current scroll position)
    const initialOffset = Math.max(0, totalHeight - window.pageYOffset);
    document.documentElement.style.setProperty('--header-card-announcement-offset', `${initialOffset}px`);
  }

  /**
   * Calculate and cache layout values used for scroll calculations
   */
  #calculateLayoutValues() {
    const { container } = this.refs;
    if (!container) return;

    const headerPadding =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-padding')) || 0;
    this.#headerPaddingPx = headerPadding * parseFloat(getComputedStyle(document.documentElement).fontSize);
    this.#hidingPoint = this.#announcementBarHeight + container.offsetHeight;
  }

  /**
   * Setup ResizeObserver to recalculate positioning on resize
   */
  #setupResizeObserver() {
    const { container } = this.refs;
    if (!container) return;

    const debouncedResizeHandler = debounce(() => {
      this.#setHeaderContainerHeight();
      this.#setAnnouncementBarHeight();
      this.#calculateLayoutValues();
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        const { megamenu: megamenus } = this.refs;
        megamenus?.forEach((menu) => menu.style.removeProperty('--content-height'));
        this.#calculateMegamenuHeights();
      }
    }, 150);

    new ResizeObserver(debouncedResizeHandler).observe(container);
  }

  // ========================================
  // Glass Mode
  // ========================================

  /**
   * Setup glass mode positioning for announcement bars
   */
  #setupGlassMode() {
    if (!this.hasAttribute('data-glass-mode-available')) {
      this.#setAnnouncementBarHeight();
      this.#setHeaderContainerHeight();
      this.#calculateLayoutValues();
      return;
    }

    const announcementBarSections = document.querySelectorAll(
      '.shopify-section--announcement-bar:has(.announcement-bar[data-glass-mode-available])',
    );

    if (announcementBarSections.length === 0) {
      document.documentElement.style.setProperty('--announcement-bar-total-height', '0px');
      this.setAttribute('data-glass-mode-ready', '');
      this.#setHeaderContainerHeight();
      this.#calculateLayoutValues();
      return;
    }

    let totalHeight = 0;
    announcementBarSections.forEach((section, index) => {
      const bar = section.querySelector('.announcement-bar');
      if (!bar || !(bar instanceof HTMLElement)) return;

      if (index > 0) {
        /** @type {HTMLElement} */ (section).style.top = `${totalHeight}px`;
      }
      totalHeight += bar.offsetHeight;
      bar.setAttribute('data-glass-mode-ready', '');
    });

    document.documentElement.style.setProperty('--announcement-bar-total-height', `${totalHeight}px`);
    this.#announcementBarHeight = totalHeight;
    this.#setHeaderContainerHeight();
    this.#calculateLayoutValues();
    this.setAttribute('data-glass-mode-ready', '');
  }

  // ========================================
  // Sticky Header
  // ========================================

  /**
   * Setup sticky header behavior
   */
  #setupStickyHeader() {
    const { container } = this.refs;
    if (!container) return;

    const isGlassMode = this.hasAttribute('data-glass-mode-available');
    this.#prevScrollPos = window.pageYOffset;

    // Initialize state
    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      this.toggleAttribute('data-sticky', window.scrollY > this.#announcementBarHeight);
      if (isGlassMode) {
        const visibleHeight = Math.max(0, this.#announcementBarHeight - window.scrollY);
        this.style.paddingTop = `${visibleHeight + this.#headerPaddingPx}px`;
        this.toggleAttribute('data-glass-mode-active', window.scrollY === 0);
      }
    } else if (isGlassMode) {
      if (window.scrollY > this.#announcementBarHeight) {
        this.setAttribute('data-sticky', '');
        this.removeAttribute('data-glass-mode-active');
      } else if (window.scrollY === 0) {
        this.setAttribute('data-glass-mode-active', '');
      }
    }

    /** @type {number | null} */
    let rafId = null;

    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

      // Update scroll direction
      if (currentScrollPos > this.#prevScrollPos) {
        this.#scrollDirection = 2; // down
      } else if (currentScrollPos < this.#prevScrollPos) {
        this.#scrollDirection = 1; // up
      }

      // Update announcement offset for sticky product card positioning
      // Decreases from announcement bar height to 0 as user scrolls that distance
      const announcementOffset = Math.max(0, this.#announcementBarHeight - currentScrollPos);
      document.documentElement.style.setProperty('--header-card-announcement-offset', `${announcementOffset}px`);

      if (isDesktop && isGlassMode) {
        // Desktop: toggle sticky state
        if (this.#scrollDirection === 2 && currentScrollPos > this.#announcementBarHeight) {
          this.setAttribute('data-sticky', '');
        } else if (this.#scrollDirection === 1 && currentScrollPos <= this.#announcementBarHeight) {
          this.removeAttribute('data-sticky');
        }
      } else if (!isDesktop) {
        // Mobile: show/hide header and update glass mode padding
        const headerOffset = isGlassMode ? this.#announcementBarHeight : container.getBoundingClientRect().top;
        const hidingPoint = isGlassMode ? this.#hidingPoint : headerOffset + container.offsetHeight;

        if (isGlassMode) {
          if (rafId) cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(() => {
            const visibleHeight = Math.max(0, this.#announcementBarHeight - currentScrollPos);
            this.style.paddingTop = `${visibleHeight + this.#headerPaddingPx}px`;
          });
        }

        const isSticky = currentScrollPos > this.#announcementBarHeight && headerOffset > 0;
        if (isSticky !== this.#headerIsSticky) {
          this.#headerIsSticky = isSticky;
          this.toggleAttribute('data-sticky', isSticky);
        }

        if (this.#scrollDirection === 2 && currentScrollPos > hidingPoint) {
          this.#setHeaderVisibility(false);
        } else if (this.#scrollDirection === 1) {
          this.#setHeaderVisibility(true);
        }

        if (isGlassMode) {
          this.toggleAttribute('data-glass-mode-active', currentScrollPos <= hidingPoint);
        }
      }

      this.#prevScrollPos = currentScrollPos;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  /**
   * Show or hide the header via transform
   * @param {boolean} visible
   */
  #setHeaderVisibility(visible) {
    const { container, panelsContainer } = this.refs;
    if (!container) return;

    const transform = visible ? 'translateY(0)' : `translateY(-${this.#hidingPoint + this.#headerPaddingPx}px)`;
    container.style.transform = transform;
    if (panelsContainer) {
      panelsContainer.style.transform = transform;
    }
  }

  // ========================================
  // Desktop Megamenu
  // ========================================

  /**
   * Setup desktop keyboard navigation for megamenu
   */
  #setupDesktopKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#megamenuOpen && window.innerWidth >= DESKTOP_BREAKPOINT) {
        e.preventDefault();
        if (this.#activeMenuItem) {
          const megamenuId = this.#activeMenuItem.getAttribute('aria-controls');
          if (megamenuId) {
            this.#removeFocusTrap(document.getElementById(megamenuId));
          }
          this.#activeMenuItem.focus();
          this.#closeMegamenu(this.#activeMenuItem);
          this.#activeMenuItem = null;
        }
      }
    });

    /** @type {NodeListOf<HTMLElement>} */
    const menuLinks = this.querySelectorAll('.apc-header__menu-list .apc-header__menu-link');

    menuLinks.forEach((link) => {
      link.addEventListener('keydown', (e) => {
        if (window.innerWidth < DESKTOP_BREAKPOINT) return;

        const hasSubmenu = link.hasAttribute('aria-controls');
        const shouldToggle = (e.key === 'Enter' && hasSubmenu) || (e.key === ' ' && hasSubmenu);
        if (!shouldToggle) return;

        e.preventDefault();

        if (this.#activeMenuItem === link) {
          const megamenuId = link.getAttribute('aria-controls');
          if (megamenuId) {
            this.#removeFocusTrap(document.getElementById(megamenuId));
          }
          this.#closeMegamenu(link);
          this.#activeMenuItem = null;
        } else {
          this.#openMegamenu(link, { trapFocus: true });
        }
      });
    });
  }

  /**
   * Open a megamenu
   * @param {HTMLElement} menuLink
   * @param {object} options
   * @param {boolean} [options.trapFocus] - Whether to trap focus (keyboard navigation)
   * @param {boolean} [options.animate] - Whether to animate switching between menus
   */
  #openMegamenu(menuLink, { trapFocus = false, animate = false } = {}) {
    // Cancel pending close and close cart notification
    this.#debouncedCloseMegamenu?.cancel();
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification instanceof CartNotification) cartNotification.close(true);

    const megamenuId = menuLink.getAttribute('aria-controls');
    if (!megamenuId) return;
    const megamenu = document.getElementById(megamenuId);
    if (!megamenu) return;

    const wasAnyOpen = this.#megamenuOpen;

    // Close previously active megamenu
    if (this.#activeMenuItem && this.#activeMenuItem !== menuLink) {
      const prevId = this.#activeMenuItem.getAttribute('aria-controls');
      if (prevId) {
        const prevMegamenu = document.getElementById(prevId);
        if (prevMegamenu) {
          if (animate) {
            const { megamenuWrapper } = this.refs;
            if (megamenuWrapper instanceof HTMLElement) {
              megamenuWrapper.setAttribute('data-transition', 'switching');
              setTimeout(() => megamenuWrapper.removeAttribute('data-transition'), MEGAMENU_SWITCH_DURATION);
            }
          }
          prevMegamenu.setAttribute('aria-expanded', 'false');
        }
      }
      this.#activeMenuItem.setAttribute('aria-expanded', 'false');
    }

    // Activate new megamenu
    this.#activeMenuItem = menuLink;
    menuLink.setAttribute('aria-expanded', 'true');
    megamenu.setAttribute('aria-expanded', 'true');

    if (window.innerWidth >= DESKTOP_BREAKPOINT) {
      this.#updateMegamenuHeight(megamenu);
    }

    // Open wrapper if first megamenu
    if (!wasAnyOpen) {
      this.#megamenuOpen = true;
      const { megamenuWrapper, menuToggle } = this.refs;
      if (megamenuWrapper instanceof HTMLElement) {
        megamenuWrapper.setAttribute('aria-expanded', 'true');
      }
      const backdrop = menuToggle?.find((el) => el.hasAttribute('data-backdrop'));
      backdrop?.setAttribute('aria-expanded', 'true');
      enableScrollLock(this);
    }

    if (trapFocus) {
      this.#trapFocusInMegamenu(megamenu);
      const firstFocusable = megamenu.querySelector('a, button, [tabindex="0"]');
      if (firstFocusable instanceof HTMLElement) {
        setTimeout(() => firstFocusable.focus(), MEGAMENU_VISIBILITY_DELAY);
      }
    } else {
      this.#showSafeTriangle();
    }
  }

  /**
   * Close a megamenu
   * @param {HTMLElement} menuLink
   */
  #closeMegamenu(menuLink) {
    const megamenuId = menuLink.getAttribute('aria-controls');
    if (!megamenuId) return;
    const megamenu = document.getElementById(megamenuId);
    if (!megamenu) return;

    menuLink.setAttribute('aria-expanded', 'false');
    megamenu.setAttribute('aria-expanded', 'false');

    this.#megamenuOpen = false;
    const { megamenuWrapper, menuToggle } = this.refs;
    if (megamenuWrapper instanceof HTMLElement) {
      megamenuWrapper.setAttribute('aria-expanded', 'false');
      megamenuWrapper.style.maxHeight = '';
    }
    const backdrop = menuToggle?.find((el) => el.hasAttribute('data-backdrop'));
    backdrop?.setAttribute('aria-expanded', 'false');

    this.#hideSafeTriangle();
    disableScrollLock(this);
  }

  /**
   * Calculate and store heights for all megamenus
   */
  #calculateMegamenuHeights() {
    if (window.innerWidth < DESKTOP_BREAKPOINT) return;
    const { megamenu: megamenus, megamenuWrapper } = this.refs;
    if (!megamenus || !megamenuWrapper || !(megamenuWrapper instanceof HTMLElement)) return;
    if (megamenus[0]?.style.getPropertyValue('--content-height')) return;

    megamenuWrapper.style.pointerEvents = 'none';
    megamenuWrapper.style.visibility = 'hidden';

    const measurements = megamenus.map((menu) => {
      const originalPosition = menu.style.position;
      const originalInset = menu.style.inset;
      menu.style.position = 'relative';
      menu.style.inset = 'auto';
      return { menu, originalPosition, originalInset };
    });

    megamenuWrapper.offsetHeight; // Force reflow

    measurements.forEach(({ menu, originalPosition, originalInset }) => {
      menu.style.setProperty('--content-height', `${menu.scrollHeight}px`);
      menu.style.position = originalPosition;
      menu.style.inset = originalInset;
    });

    megamenuWrapper.style.pointerEvents = '';
    megamenuWrapper.style.visibility = '';
  }

  /**
   * Update megamenu wrapper height
   * @param {HTMLElement} megamenu
   */
  #updateMegamenuHeight(megamenu) {
    const { megamenuWrapper } = this.refs;
    if (!(megamenuWrapper instanceof HTMLElement)) return;
    const contentHeight = megamenu.style.getPropertyValue('--content-height');
    if (contentHeight) {
      megamenuWrapper.style.maxHeight = contentHeight;
    }
  }

  /**
   * Trap focus inside megamenu
   * @param {HTMLElement} megamenu
   */
  #trapFocusInMegamenu(megamenu) {
    const focusableElements = megamenu.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.#focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        if (lastFocusable instanceof HTMLElement) lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        if (firstFocusable instanceof HTMLElement) firstFocusable.focus();
      }
    };

    megamenu.addEventListener('keydown', this.#focusTrapHandler);
  }

  /**
   * Remove focus trap from megamenu
   * @param {HTMLElement | null} megamenu
   */
  #removeFocusTrap(megamenu) {
    if (this.#focusTrapHandler && megamenu) {
      megamenu.removeEventListener('keydown', this.#focusTrapHandler);
      this.#focusTrapHandler = null;
    }
  }

  // ========================================
  // Safe Triangle (Megamenu Hover Zone)
  // ========================================

  #showSafeTriangle() {
    const { safeTriangle } = this.refs;
    if (!safeTriangle || window.innerWidth < DESKTOP_BREAKPOINT) return;

    safeTriangle.setAttribute('data-active', '');
    this.#boundMouseMoveHandler = /** @type {(e: MouseEvent) => void} */ (this.#handleMouseMove.bind(this));
    window.addEventListener('mousemove', this.#boundMouseMoveHandler);
  }

  #hideSafeTriangle() {
    const { safeTriangle } = this.refs;
    if (!safeTriangle) return;

    safeTriangle.removeAttribute('data-active');
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    if (this.#boundMouseMoveHandler) {
      window.removeEventListener('mousemove', this.#boundMouseMoveHandler);
      this.#boundMouseMoveHandler = null;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove(event) {
    this.#mousePosition = { x: event.clientX, y: event.clientY };
    if (this.#rafId) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#updateSafeTriangle();
      this.#rafId = null;
    });
  }

  #updateSafeTriangle() {
    const { safeTriangle, safeTrianglePath, megamenuWrapper, megamenuContainer } = this.refs;
    if (!safeTriangle || !safeTrianglePath || !(megamenuWrapper instanceof HTMLElement)) return;
    if (!(megamenuContainer instanceof HTMLElement)) return;

    const activeMegamenu = megamenuWrapper.querySelector('[aria-expanded="true"]');
    if (!activeMegamenu) {
      this.#hideSafeTriangle();
      return;
    }

    const megamenuRect = megamenuContainer.getBoundingClientRect();
    const { x: mouseX, y: mouseY } = this.#mousePosition;

    // Only activate when cursor is in right 50% of active menu item
    if (this.#activeMenuItem) {
      const menuItemRect = this.#activeMenuItem.getBoundingClientRect();
      if (mouseX < menuItemRect.left + menuItemRect.width * 0.5) {
        safeTriangle.removeAttribute('data-active');
        return;
      }
    }

    const svgWidth = megamenuRect.left - mouseX + SAFE_TRIANGLE_PADDING;
    if (svgWidth <= 0) {
      safeTriangle.removeAttribute('data-active');
      return;
    }

    safeTriangle.setAttribute('data-active', '');
    safeTriangle.style.width = `${svgWidth}px`;
    safeTriangle.style.height = `${megamenuRect.height}px`;
    safeTriangle.style.top = `${megamenuRect.top}px`;
    safeTriangle.style.left = `${mouseX - SAFE_TRIANGLE_PADDING}px`;

    const relativeMouseY = mouseY - megamenuRect.top;
    safeTrianglePath.setAttribute('d', `M 0,${relativeMouseY} L ${svgWidth},0 L ${svgWidth},${megamenuRect.height} Z`);
  }

  // ========================================
  // Mobile Menu
  // ========================================

  /**
   * Check if mobile menu is open
   * @returns {boolean}
   */
  #isMobileMenuOpen() {
    return this.refs.panelsWrapper?.getAttribute('aria-expanded') === 'true';
  }

  #setupMobileMenu() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#isMobileMenuOpen()) {
        this.toggleMobileMenu(e);
      }
    });
  }

  /**
   * Toggle mobile menu open/closed
   * @param {Event} event
   */
  toggleMobileMenu(event) {
    event.preventDefault();
    if (this.#isMobileMenuOpen()) {
      this.#closeMobileMenu();
    } else {
      this.#openMobileMenu();
    }
  }

  #openMobileMenu() {
    const { panelsWrapper, menuToggle } = this.refs;
    if (!panelsWrapper) return;

    if (this.hasAttribute('data-glass-mode-available')) {
      this.toggleAttribute('data-glass-mode-active', false);
    }

    panelsWrapper.setAttribute('aria-expanded', 'true');
    menuToggle?.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    enableScrollLock(this);
  }

  #closeMobileMenu() {
    const { panelsWrapper, menuToggle } = this.refs;
    if (!panelsWrapper) return;

    if (this.hasAttribute('data-glass-mode-available')) {
      this.toggleAttribute('data-glass-mode-active', window.scrollY <= this.#hidingPoint);
    }

    panelsWrapper.setAttribute('aria-expanded', 'false');
    menuToggle?.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
    this.#switchPanel('mobile-panel-main');
    disableScrollLock(this);
  }

  /**
   * Navigate to a sub-panel
   * @param {Event} event
   */
  navigateToPanel(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const trigger = event.target.closest('[ref="panelTrigger[]"]');
    const panelId = trigger?.getAttribute('aria-controls');
    if (panelId) this.#switchPanel(panelId);
  }

  /**
   * Navigate back to main panel
   * @param {Event} event
   */
  navigateBackToMain(event) {
    event.preventDefault();
    this.#switchPanel('mobile-panel-main');
  }

  /**
   * Switch between panels
   * @param {string} targetPanelId
   */
  #switchPanel(targetPanelId) {
    if (!this.#currentPanelId) return;

    const currentPanel = document.getElementById(this.#currentPanelId);
    const targetPanel = document.getElementById(targetPanelId);
    const { panelsWrapper, panelTrigger } = this.refs;

    if (!targetPanel || !panelsWrapper) return;

    panelsWrapper.setAttribute('data-transition', targetPanelId === 'mobile-panel-main' ? 'to-main' : 'to-sub');
    currentPanel?.setAttribute('aria-hidden', 'true');
    targetPanel.setAttribute('aria-hidden', 'false');
    this.#currentPanelId = targetPanelId;

    // Update trigger states
    const triggers = Array.isArray(panelTrigger) ? panelTrigger : panelTrigger ? [panelTrigger] : [];
    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-controls') === targetPanelId ? 'true' : 'false');
    });

    const focusTarget = targetPanel.querySelector('button, a, [tabindex="0"]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  // ========================================
  // Public Event Handlers (called from template)
  // ========================================

  /**
   * Activate megamenu on hover
   * @param {PointerEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const menuLink = event.target.querySelector('[ref="menuitem"]');
    if (!menuLink || !(menuLink instanceof HTMLElement)) return;

    this.#debouncedCloseMegamenu?.cancel();

    if (this.#activeMenuItem === menuLink) return;

    const { safeTriangle } = this.refs;
    if (safeTriangle?.hasAttribute('data-active')) return;

    this.#openMegamenu(menuLink, { animate: true });
  };

  /**
   * Keep megamenu active when hovering over it
   */
  keepActive = () => {
    this.#debouncedCloseMegamenu?.cancel();
    this.#hideSafeTriangle();
  };

  /**
   * Deactivate megamenu on hover out
   * @param {PointerEvent} event
   */
  deactivate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#debouncedCloseMegamenu?.();
  };

  /**
   * Handle pointer entering safe triangle
   */
  handleSafeTriangleEnter = () => {
    this.#debouncedCloseMegamenu?.cancel();
  };

  /**
   * Handle pointer leaving safe triangle
   */
  handleSafeTriangleLeave = () => {
    this.#debouncedCloseMegamenu?.();
  };
}

if (!customElements.get('apc-header')) {
  customElements.define('apc-header', APCTFHeader);
}
